import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { FloatingCall } from "@/components/CallButton";
import LiveChat from "@/components/LiveChat";
import QuotePopup from "@/components/QuotePopup";
import useGoogleAnalytics from "@/hooks/useGoogleAnalytics";

// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import WriteReview from "@/pages/WriteReview";
import ReferAndEarn from "@/pages/ReferAndEarn";
import CustomizeTour from "@/pages/CustomizeTour";
import { CancellationPolicy, TermsAndConditions, PrivacyPolicy, RefundPolicy } from "@/pages/LegalPages";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Packages from "@/pages/Packages";
import PackageDetail from "@/pages/PackageDetail";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MyBookings from "@/pages/MyBookings";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminInstaFeed from "@/pages/admin/AdminInstaFeed";

// Route Guards
const RequireAuth = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
};

const RequireAdmin = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner"></div></div>;
  if (!isAdmin) return <Navigate to="/admin" replace />;
  return children;
};

// Show floating action buttons only on public/customer pages (not login/admin)
const FloatingActionsWrapper = () => {
  const location = useLocation();
  const hiddenPaths = ["/login", "/register", "/forgot-password", "/reset-password", "/admin", "/auth/callback"];
  const isAdminRoute = location.pathname.startsWith("/admin");
  const shouldHide = hiddenPaths.includes(location.pathname) || isAdminRoute;
  if (shouldHide) return null;
  return (<><FloatingCall /><FloatingWhatsApp /><LiveChat /><QuotePopup /></>);
};

// Handle Google OAuth callback via URL fragment BEFORE normal routing
const AppRouter = () => {
  const location = useLocation();
  useGoogleAnalytics();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
        {/* Public routes — no login needed */}
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/packages/:id" element={<PackageDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/customize-tour" element={<CustomizeTour />} />
        <Route path="/write-review" element={<WriteReview />} />
        <Route path="/cancellation-policy" element={<CancellationPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />

        {/* Auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected user routes */}
        <Route path="/my-bookings" element={<RequireAuth><MyBookings /></RequireAuth>} />
        <Route path="/refer" element={<RequireAuth><ReferAndEarn /></RequireAuth>} />
        <Route path="/payment-success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
        <Route path="/payment-cancel" element={<RequireAuth><PaymentCancel /></RequireAuth>} />

        {/* Admin routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="/admin/packages" element={<RequireAdmin><AdminPackages /></RequireAdmin>} />
        <Route path="/admin/bookings" element={<RequireAdmin><AdminBookings /></RequireAdmin>} />
        <Route path="/admin/inquiries" element={<RequireAdmin><AdminInquiries /></RequireAdmin>} />
        <Route path="/admin/payments" element={<RequireAdmin><AdminPayments /></RequireAdmin>} />
        <Route path="/admin/testimonials" element={<RequireAdmin><AdminTestimonials /></RequireAdmin>} />
        <Route path="/admin/insta-feed" element={<RequireAdmin><AdminInstaFeed /></RequireAdmin>} />
      </Routes>
      <FloatingActionsWrapper />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
